<template>
  <article class="workspace-card form-card email-settings">
    <h2>Account email</h2>
    <p v-if="loading" role="status">Loading email settings…</p>
    <template v-else-if="loaded">
    <p v-if="state.verified_email">Verified email: <strong>{{ state.verified_email }}</strong></p>
    <p v-else>No verified email. Email password recovery becomes available after you verify your email.</p>
    <p v-if="state.pending_email">Pending verification: <strong>{{ state.pending_email }}</strong>. Open the verification email to finish binding. {{ state.verified_email ? 'Your existing verified email remains active until confirmation.' : 'Password recovery is not available until you verify an email.' }}</p>
    <p v-if="state.delivery_status">Delivery status: {{ state.delivery_status }} (queued or sent does not guarantee inbox delivery).</p>
    <p v-if="!state.enabled">Email recovery is unavailable. Contact your teacher or administrator.</p>
    <form v-else-if="state.self_binding_allowed" @submit.prevent="bind">
      <h3>{{ state.verified_email ? 'Change email' : state.pending_email ? 'Use a different email' : 'Bind my email' }}</h3>
      <label>Email address<input v-model="email" class="ui-input" type="email" maxlength="254" autocomplete="email" required /></label>
      <label>Current password<input v-model="password" class="ui-input" type="password" maxlength="256" autocomplete="current-password" required /></label>
      <button class="btn-primary" :disabled="busy" type="submit">{{ busy ? 'Sending…' : 'Send verification email' }}</button>
    </form>
    <div v-else class="email-restricted">
      <p>Self-service email binding has been disabled for this account. Contact your teacher or administrator for help.</p>
      <p v-if="helpRequested" role="status">Help requested. Your teacher or administrator will review it.</p>
    </div>
    </template>
    <div class="email-actions">
      <button v-if="loaded && state.enabled && !state.self_binding_allowed && !helpRequested" type="button" class="ui-button" :disabled="busy || loading" @click="requestHelp">Request help</button>
      <button v-if="loaded && state.pending_email && state.enabled && state.self_binding_allowed" type="button" class="ui-button" :disabled="busy || loading" @click="resend">Resend verification email</button>
      <button type="button" class="ui-button" :disabled="busy || loading" @click="refresh">{{ loading ? 'Refreshing…' : 'Refresh email status' }}</button>
    </div>
    <p v-if="message" class="ui-notice" role="status">{{ message }}</p>
  </article>
</template>
<script setup>
import { onMounted, ref } from 'vue'
import { accountRequest } from '../account-email-client'
import { useAuthStore } from '../stores/auth'
const auth = useAuthStore()
const state = ref({}), email = ref(''), password = ref(''), busy = ref(false), message = ref('')
const loading = ref(false), loaded = ref(false), helpRequested = ref(false)
async function load() {
  loading.value = true
  try { state.value = await accountRequest('auth/email'); loaded.value = true }
  finally { loading.value = false }
}
async function refresh() {
  message.value = ''
  try { await load(); message.value = 'Email status refreshed.' }
  catch (error) { loaded.value = false; message.value = error.message }
}
async function requestHelp() {
  busy.value = true; message.value = ''
  try {
    const result = await accountRequest('auth/password-reset-requests', { username: auth.user.username, class_note: 'Please review the disabled self-service email binding permission.' })
    message.value = result.message; helpRequested.value = true
  } catch (error) { message.value = error.message }
  finally { busy.value = false }
}
async function perform(path, data) {
  busy.value = true; message.value = ''
  try {
    await accountRequest(path, data)
    message.value = 'Verification email queued. Check your inbox and spam folder.'
    email.value = ''
    try { await load() } catch { message.value += ' Could not refresh the status; use Refresh email status to check again.' }
  }
  catch (error) { message.value = error.message }
  finally { busy.value = false; password.value = '' }
}
function bind() { return perform('auth/email/bind', { email: email.value, current_password: password.value }) }
function resend() { return perform('auth/email/resend', {}) }
onMounted(refresh)
</script>
<style scoped>
.email-settings { margin-top: var(--space-6); overflow-wrap: anywhere; }
.email-settings form, .email-settings label { display: grid; gap: var(--space-3); }
.email-settings form { max-width: 32rem; margin-bottom: var(--space-4); }
.email-settings button { min-height: 44px; }
.email-actions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-3); margin-top: var(--space-4); }
.email-settings .ui-notice { margin-top: var(--space-4); }
@media (max-width: 600px) {
  .email-actions { flex-direction: column; align-items: stretch; }
  .email-actions button, .email-settings form button { width: 100%; }
}
</style>
